import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { validatePassword, PASSWORD_HELPER_TEXT } from '../../utils/passwordValidation';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { AuthBackground } from './auth/AuthBackground';
import { BrandHero } from './auth/BrandHero';
import { ChargingProgressBar } from './auth/ChargingProgressBar';
import { otpService } from '../../services/otpService';
import { ArrowLeft, Mail, Zap, Lock, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';

type Step = 'email' | 'otp' | 'password' | 'success';

const STEP_INDEX: Record<Step, number> = { email: 1, otp: 2, password: 3, success: 3 };

export function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [direction, setDirection] = useState(1);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const goToStep = (nextStep: Step) => {
    const currentIdx = STEP_INDEX[step];
    const nextIdx = STEP_INDEX[nextStep];
    setDirection(nextIdx >= currentIdx ? 1 : -1);
    setStep(nextStep);
  };

  // Auto-navigate to login after success
  useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(() => navigate('/login', { replace: true }), 2500);
      return () => clearTimeout(timer);
    }
  }, [step, navigate]);

  const startCountdown = () => {
    setResendCountdown(60);
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setIsLoading(true);
    try {
      await otpService.requestPasswordReset(email.trim());
      goToStep('otp');
      startCountdown();
    } catch {
      goToStep('otp');
      startCountdown();
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = (value: string) => {
    setOtpCode(value);
    if (value.length === 6) {
      setError(null);
      goToStep('password');
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await otpService.requestPasswordReset(email.trim());
      startCountdown();
      setOtpCode('');
      setError(null);
    } catch {
      // Ignore errors
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await otpService.confirmPasswordReset(email.trim(), otpCode, newPassword, confirmPassword);
      goToStep('success');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to reset password';
      if (msg.toLowerCase().includes('otp') || msg.toLowerCase().includes('expired')) {
        goToStep('otp');
        setOtpCode('');
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'otp') goToStep('email');
    else if (step === 'password') goToStep('otp');
    else navigate('/login');
  };

  const slideVariants = {
    enter: (dir: number) => ({ x: dir * 300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir * -300, opacity: 0 }),
  };

  const slideTransition = { type: 'spring' as const, damping: 25, stiffness: 200 };

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden">
      <AuthBackground />

      {/* Header */}
      <div className="relative z-10 flex items-center px-4 pt-[env(safe-area-inset-top)] py-3">
        {step !== 'success' && (
          <button
            onClick={handleBack}
            className="p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <span className="ml-2 text-lg font-medium text-foreground">
          {step === 'email' && 'Forgot Password'}
          {step === 'otp' && 'Enter OTP'}
          {step === 'password' && 'New Password'}
          {step === 'success' && 'All Done'}
        </span>
      </div>

      {/* Brand + Progress */}
      <div className="relative z-10 px-6 pt-4 pb-6 flex flex-col items-center">
        <BrandHero compact />
        {step !== 'success' && (
          <div className="w-full max-w-xs mt-6">
            <ChargingProgressBar
              currentStep={STEP_INDEX[step]}
              totalSteps={3}
              labels={['Email', 'Verify', 'Reset']}
            />
          </div>
        )}
      </div>

      {/* Step Content */}
      <div className="relative z-10 flex-1 px-6 flex flex-col items-center overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          {/* Step 1: Email */}
          {step === 'email' && (
            <motion.div
              key="email"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="w-full max-w-sm flex flex-col items-center"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] flex items-center justify-center mb-4 shadow-lg shadow-[#8bc43e]/25">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-foreground mb-2">Reset your password</h1>
              <p className="text-sm text-muted-foreground text-center mb-8 max-w-xs">
                Enter the email address associated with your account and we'll send you a verification code
              </p>

              <form onSubmit={handleSendOtp} className="w-full space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#7ab32e] hover:to-[#5f8a26] text-white rounded-xl font-semibold shadow-lg shadow-[#8bc43e]/25 border-0"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              </form>

              <p className="mt-6 text-sm text-muted-foreground">
                Remember your password?{' '}
                <Link to="/login" className="text-[#8bc43e] font-semibold hover:underline">
                  Sign in
                </Link>
              </p>
            </motion.div>
          )}

          {/* Step 2: OTP */}
          {step === 'otp' && (
            <motion.div
              key="otp"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="w-full max-w-sm flex flex-col items-center"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] flex items-center justify-center mb-4 shadow-lg shadow-[#8bc43e]/25">
                <Zap className="w-8 h-8 text-white fill-white" />
              </div>
              <h1 className="text-xl font-semibold text-foreground mb-2">Verify your email</h1>
              <p className="text-sm text-muted-foreground text-center mb-2">
                We sent a 6-digit code to
              </p>
              <p className="text-sm font-semibold text-foreground mb-8">
                {email}
              </p>

              <div className="mb-6">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={handleVerifyOtp}
                  disabled={isLoading}
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="w-12 h-14 text-lg font-semibold border-2 rounded-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error && (
                <div className="w-full text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md mb-4">
                  {error}
                </div>
              )}

              <div className="text-center">
                {resendCountdown > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Resend code in <span className="font-semibold text-foreground">{resendCountdown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={isLoading}
                    className="text-sm text-[#8bc43e] font-semibold hover:underline disabled:opacity-50"
                  >
                    Resend Code
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: New Password */}
          {step === 'password' && (
            <motion.div
              key="password"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="w-full max-w-sm flex flex-col items-center"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] flex items-center justify-center mb-4 shadow-lg shadow-[#8bc43e]/25">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-foreground mb-2">Set new password</h1>
              <p className="text-sm text-muted-foreground text-center mb-8">
                Create a strong password for your account
              </p>

              <form onSubmit={handleResetPassword} className="w-full space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
                      autoComplete="new-password"
                      disabled={isLoading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{PASSWORD_HELPER_TEXT}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-new-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                      autoComplete="new-password"
                      disabled={isLoading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#7ab32e] hover:to-[#5f8a26] text-white rounded-xl font-semibold shadow-lg shadow-[#8bc43e]/25 border-0"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Success State */}
          {step === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              className="flex flex-col items-center pt-8"
            >
              <motion.div
                className="w-20 h-20 rounded-full bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] flex items-center justify-center shadow-lg shadow-[#8bc43e]/30 mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.2 }}
              >
                <CheckCircle2 className="w-10 h-10 text-white" />
              </motion.div>
              <motion.h2
                className="text-2xl font-bold text-foreground mb-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Password Reset!
              </motion.h2>
              <motion.p
                className="text-muted-foreground text-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                Your charging account is secured
              </motion.p>
              <motion.p
                className="text-xs text-muted-foreground mt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                Redirecting to sign in...
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default ForgotPasswordScreen;
